
import React, { useEffect } from 'react';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useEvents } from '@/hooks/useEvents';
import { EventsList } from '@/components/Events/EventsList';
import { PricingEvent } from '@/store/eventsSlice';
import { ChangeType } from '@/utils/eventLabels';
import Cookies from 'js-cookie';
import { handleToggleEvent } from '@/utils/swipeEntriesHelper';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { toast } from "sonner";
import { pageSlugToCompanySlug, getCategory } from '@/utils/companiesHelper';
import { versionToQuarter } from '@/utils/quarterlyEventsHelper';

const Events = () => {
  const events = useAppSelector(state => state.events.events);
  const quarterlyEvents = useAppSelector(state => state.quarterlyEvents.events);
  const loading = useAppSelector(state => state.events.loading);
  const companies = useAppSelector(state => state.supaCompanies.companies);
  const swipefileLoading = false;
  
  // Use the events hook to lazy load events when this page mounts
  const { fetchEvents } = useEvents(false); // Disable automatic loading

  // Load events when this component mounts
  useEffect(() => {
    if (events.length === 0 && companies.length > 0) {
      console.log('🔄 [Events] Lazy loading events for Events page');
      fetchEvents();
    }
  }, [companies.length]); // Only run when companies are loaded

  // Load initial filter values from cookies or use defaults
  const getInitialFilters = () => {
    const savedEventTypes = Cookies.get('eventFilters_eventTypes');
    const savedQuarters = Cookies.get('eventFilters_quarters');
    const savedCategories = Cookies.get('eventFilters_categories');

    return {
      eventTypes: savedEventTypes ? JSON.parse(savedEventTypes) as ChangeType[] : ['All' as ChangeType],
      quarters: savedQuarters ? JSON.parse(savedQuarters) as string[] : ['All'],
      categories: savedCategories ? JSON.parse(savedCategories) as string[] : ['All']
    };
  };

  const handleEventToggle = async (event: PricingEvent): Promise<void> => {
    // Project functionality removed - no toggle action needed
    return;
  };

  const handleFilterChange = (filterType: 'eventTypes' | 'quarters' | 'categories', values: string[]) => {
    // Save to cookies with 30 days expiration
    Cookies.set(`eventFilters_${filterType}`, JSON.stringify(values), { expires: 30 });
  };

  const handleExportCSV = (filteredEvents: PricingEvent[]) => {
    try {
      // Create CSV headers based on event attributes
      const headers = [
        'slug',
        'quarter',
        'year',
        'v1',
        'v2',
        'plansChanged',
        'priceIncreased',
        'priceDecreased',
        'priceAvailbilityChanged',
        'introDiscount',
        'displayChanged',
        'category',
        'company'
      ];

      const allApprovedEvents = quarterlyEvents;

      /*

export interface QuarterlyEvent {
  slug: string;
  quarter: string;
  v1: string;
  v2: string;
  has_changes: boolean;
  plans_changed: boolean;
  price_increased: boolean;
  price_decreased: boolean;
  price_availability_changed: boolean;
  display_changed: boolean;
  intro_discount: boolean;
  changes: string[];
  v1_pi_url?: string;
  v2_pi_url?: string;
  v1_url?: string;
  v2_url?: string;
}
  */

      const getYear = (version: string) => {
        return version.substring(0, 4);
      }

      const getQuarter = (version: string) => {
        const q = versionToQuarter(version);
        return q.slice(-2);
      }

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...allApprovedEvents.map(event => {
          return [
            event.slug,
            getQuarter(event.v1),
            getYear(event.v1),
            event.v1,
            event.v2,
            event.plans_changed || false,
            event.price_increased || false,
            event.price_decreased || false,
            event.price_availability_changed || false,
            event.intro_discount || false,
            event.display_changed || false,
            getCategory(event.slug) || '',
            pageSlugToCompanySlug(event.slug) || '' // Using our new helper function
          ].join(',');
        })
      ].join('\n');

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `pricing-events-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`${filteredEvents.length} events exported to CSV`);
    } catch (error) {
      console.error('❌ [Events] Error exporting CSV:', error);
      toast.error('Failed to export events to CSV');
    }
  };

  const isLoading = loading || swipefileLoading;

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <p className="text-gray-600">Loading events...</p>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-0">
        <div className="space-y-4">
          <EventsList
            events={events}
            swipefileEntries={[]}
            onToggleEvent={handleEventToggle}
            initialFilters={getInitialFilters()}
            onFilterChange={handleFilterChange}
            onExportCSV={handleExportCSV}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1400px] mx-auto px-8">
        <div className="py-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-8">Pricing Events</h1>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Events;
